import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Camera, Upload, MapPin, Navigation, CheckCircle, AlertTriangle,
  User, Home, Phone, Image as ImageIcon, FileText, Clock, Building2, Trash2, Eye,
  List, Plus, RefreshCw, Briefcase, GraduationCap, FileCheck, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Base photo types for all check types
const BASE_PHOTO_TYPES = [
  { id: 'fe_selfie', label: 'Field Executive Selfie', required: true, description: 'Your selfie at the location' },
  { id: 'verifier_id_proof', label: 'Verifier ID Proof', required: true, description: 'Your ID card/proof' }
];

// Address specific photos
const ADDRESS_PHOTO_TYPES = [
  { id: 'house_photo_1', label: 'House Photo 1', required: true, description: 'Front view of the residence' },
  { id: 'house_photo_2', label: 'House Photo 2', required: true, description: 'Another angle of the house' },
  { id: 'landmark_photo_1', label: 'Landmark Photo 1', required: true, description: 'Nearby landmark for location reference' },
  { id: 'landmark_photo_2', label: 'Landmark Photo 2', required: true, description: 'Another nearby landmark' }
];

// Employment/Education document photos
const DOCUMENT_PHOTO_TYPES = [
  { id: 'verified_doc_1', label: 'Verified Document 1', required: false, description: 'Photo of verified document' },
  { id: 'verified_doc_2', label: 'Verified Document 2', required: false, description: 'Additional document photo' }
];

const RESIDENCE_STATUS_OPTIONS = [
  'Own House',
  'Rented House',
  'Paying Guest',
  'Company Accommodation',
  'Hostel',
  "Relative's House",
  'Other'
];

// Detect check type category
const getCheckCategory = (checkType) => {
  const type = (checkType || '').toLowerCase();
  if (type.includes('address') || type.includes('residence')) return 'address';
  if (type.includes('employment') || type.includes('experience')) return 'employment';
  if (type.includes('education') || type.includes('degree') || type.includes('qualification')) return 'education';
  if (type.includes('criminal') || type.includes('court')) return 'criminal';
  return 'address'; // default
};

export default function FieldVerificationApp() {
  const { caseId, checkId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [suggestingPoliceStation, setSuggestingPoliceStation] = useState(false);

  const [caseData, setCaseData] = useState(null);
  const [checkData, setCheckData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [checkCategory, setCheckCategory] = useState('address');
  const [insufficiencyBlocked, setInsufficiencyBlocked] = useState(false);
  
  // FE Dashboard state
  const [showDashboard, setShowDashboard] = useState(false);
  const [feAssignments, setFeAssignments] = useState([]);
  const [feToken, setFeToken] = useState(localStorage.getItem('fe_token') || '');
  
  // Camera capture
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [captureTarget, setCaptureTarget] = useState(null);
  
  // Verifier form state
  const [verifierForm, setVerifierForm] = useState({
    // Verifier Details
    residence_confirmed: '',  // Residence Confirmed at Address? - syncs with field_remarks
    person_met: '',  // Person Met (was Your Name)
    verifier_name: '',  // Field Executive Name (for validation)
    // Veecheck Jun 2026 — separate FE-entered name. Used as the
    // Executive Name on the PDF when the vendor portal didn't supply a
    // Field Agent Name on assignment. Mandatory on the form.
    fe_name: '',
    relationship: '',  // Relationship with Candidate
    person_contact: '',  // Your Contact Number
    verifier_contact: '',  // Alias for person_contact
    
    // Verification Details
    residence_type: '',  // Type of Residence
    building_type: '',  // House/Building Type
    address_type: '',  // Address Type
    period_of_stay_from: '',  // Period of Stay From
    period_of_stay_to: '',  // Period of Stay To
    // Veecheck Jun 2026 — Nearest Police Station + Marital Status now
    // captured on the default field form and rendered into the BGV PDF
    // (previously hard-coded to NA).
    nearest_police_station: '',
    marital_status: '',
    neighbor_confirmed: '',  // Neighbor Confirmed Candidate's Residence?
    neighbor_name: '',  // Neighbor name if confirmed
    
    // GPS & Location
    gps_coordinates: '',
    
    // Remarks
    overall_remarks: '',
    
    // Ethics declaration
    ethics_declaration: false
  });

  // Veecheck Jun 2026 — progress completion %. Counts the key required
  // fields the FE has to fill across the form so they can see how close
  // they are to a valid submission. Placed AFTER verifierForm and
  // photos useStates because the IIFE reads them — otherwise we'd hit
  // a TDZ ReferenceError ("Cannot access 'verifierForm' before
  // initialization").
  const progressPct = (() => {
    const v = verifierForm || {};
    const steps = [
      !!v.residence_confirmed,             // 1. Confirmed at address selection
      !!v.person_met,                       // 2. Person met / Verifier name
      !!v.person_contact,                   // 3. Contact number
      v.residence_confirmed === 'yes'
        ? (!!v.period_of_stay_from && !!v.period_of_stay_to)
        : true,                             // 4. Period of stay (only on yes)
      v.residence_confirmed === 'yes'
        ? !!v.residence_type
        : true,                             // 5. Residence type (only on yes)
      photos.length > 0,                    // 6. At least 1 photo uploaded
      !!v.fe_name,                          // 7. Field Executive Name
      !!v.ethics_declaration,               // 8. Declaration
    ];
    const done = steps.filter(Boolean).length;
    return Math.round((done / steps.length) * 100);
  })();

  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [photoForm, setPhotoForm] = useState({
    file_url: '',
    description: ''
  });
  
  // Photo preview modal state
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Auto-capitalize text input (capitalize first letter of each word)
  const autoCapitalize = (text) => {
    if (!text) return '';
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Handle text input with auto-capitalization
  const handleTextInput = (field, value) => {
    const capitalizedValue = autoCapitalize(value);
    setVerifierForm(prev => ({ ...prev, [field]: capitalizedValue }));
  };

  // Handle photo preview
  const handleViewPhoto = (photo) => {
    setPreviewPhoto(photo);
    setShowPreviewModal(true);
  };
  
  // Handle photo retake (delete and allow re-upload)
  const handleRetakePhoto = async (annexureId, photoTypeId) => {
    try {
      await axios.delete(`/field-verification/${caseId}/${checkId}/annexures/${annexureId}`);
      setPhotos(prev => prev.filter(p => p.id !== annexureId));
      setShowPreviewModal(false);
      setPreviewPhoto(null);
      toast.success('Photo removed. You can now capture/upload a new one.');
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  // Get photo types based on check category
  const getPhotoTypes = () => {
    const types = [...BASE_PHOTO_TYPES];
    if (checkCategory === 'address' || checkCategory === 'criminal') {
      types.unshift(...ADDRESS_PHOTO_TYPES);
    }
    if (checkCategory === 'employment' || checkCategory === 'education') {
      types.unshift(...ADDRESS_PHOTO_TYPES.slice(0, 2)); // Just 2 location photos
      types.push(...DOCUMENT_PHOTO_TYPES);
    }
    return types;
  };

  // Fetch case data and photos
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch case details (public endpoint for field app)
        const [caseRes, annexuresRes] = await Promise.all([
          axios.get(`/field-verification/${caseId}/${checkId}`),
          axios.get(`/cases/${caseId}/checks/${checkId}/annexures`).catch(() => ({ data: [] }))
        ]);
        setCaseData(caseRes.data.case);
        setCheckData(caseRes.data.check);
        setEmployeeData(caseRes.data.employee);
        setPhotos(annexuresRes.data || []);

        // Veecheck Jun 28, 2026 — Hydrate the form with previously
        // saved field_verification data so a returning FE / QC viewer
        // sees what was entered (instead of an empty form). Works for
        // both in-progress and completed cases.
        const fvSaved = (caseRes.data.check && caseRes.data.check.field_verification) || {};
        const avSaved = (caseRes.data.check && caseRes.data.check.address_verification) || {};
        const _pick = (...vals) => {
          for (const v of vals) {
            if (v !== undefined && v !== null && v !== '') return v;
          }
          return '';
        };
        if (Object.keys(fvSaved).length || Object.keys(avSaved).length) {
          setVerifierForm(prev => ({
            ...prev,
            residence_confirmed: _pick(fvSaved.residence_confirmed, avSaved.field_residence_confirmed, prev.residence_confirmed),
            person_met: _pick(fvSaved.person_met_name, fvSaved.person_met, avSaved.field_person_met_name, prev.person_met),
            verifier_name: _pick(fvSaved.verifier_name, prev.verifier_name),
            fe_name: _pick(fvSaved.fe_name, avSaved.field_fe_name, prev.fe_name),
            relationship: _pick(fvSaved.person_met_relation, prev.relationship),
            person_contact: _pick(fvSaved.person_met_contact, fvSaved.verifier_contact, prev.person_contact),
            verifier_contact: _pick(fvSaved.verifier_contact, fvSaved.person_met_contact, prev.verifier_contact),
            residence_type: _pick(fvSaved.residence_type, avSaved.field_residence_status, prev.residence_type),
            building_type: _pick(fvSaved.building_type, prev.building_type),
            address_type: _pick(fvSaved.address_type, prev.address_type),
            period_of_stay_from: _pick(fvSaved.period_of_stay_from, avSaved.field_period_of_stay_from, prev.period_of_stay_from),
            period_of_stay_to: _pick(fvSaved.period_of_stay_to, avSaved.field_period_of_stay_to, prev.period_of_stay_to),
            nearest_police_station: _pick(fvSaved.nearest_police_station, avSaved.field_nearest_police_station, prev.nearest_police_station),
            marital_status: _pick(fvSaved.marital_status, avSaved.field_marital_status, prev.marital_status),
            neighbor_confirmed: _pick(fvSaved.neighbor_confirmed, fvSaved.neighbour_confirmed, prev.neighbor_confirmed),
            neighbor_name: _pick(fvSaved.neighbor_name, prev.neighbor_name),
            overall_remarks: _pick(fvSaved.overall_remarks, fvSaved.remarks, prev.overall_remarks),
            gps_coordinates: _pick(fvSaved.gps_coordinates, prev.gps_coordinates),
          }));
        }

        // Veecheck Jun 28, 2026 — Pre-fill the Field Executive Name from
        // whatever the external vendor entered on assignment (if any).
        // If a previous draft already exists in the saved verification
        // (e.g. the FE returned to edit), prefer that. Falls through
        // cleanly when neither is set so the FE just types their name.
        const vendorAgent = (caseRes.data.check?.field_agent_name || '').trim();
        const previousFeName = (
          (caseRes.data.check?.field_verification && caseRes.data.check.field_verification.fe_name)
          || (caseRes.data.check?.address_verification && caseRes.data.check.address_verification.field_fe_name)
          || ''
        );
        const prefillFe = previousFeName || vendorAgent;
        if (prefillFe) {
          setVerifierForm(prev => prev.fe_name ? prev : { ...prev, fe_name: prefillFe });
        }
        
        // Block if insufficiency raised
        if (caseRes.data.has_insufficiency) {
          toast.error('Insufficiency raised for this case. Please ask admin to clear insufficiency before updating.');
          setInsufficiencyBlocked(true);
          setLoading(false);
          return;
        }
        
        // Detect check category
        const category = getCheckCategory(caseRes.data.check?.check_type);
        setCheckCategory(category);
        
        // Save FE token for dashboard
        if (caseRes.data.check?.field_token) {
          setFeToken(caseRes.data.check.field_token);
          localStorage.setItem('fe_token', caseRes.data.check.field_token);
        }
      } catch (error) {
        console.error('Failed to load verification data:', error);
        toast.error('Failed to load verification data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    refreshLocation();
  }, [caseId, checkId]);

  // Refresh GPS location
  const refreshLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
            accuracy: position.coords.accuracy
          };
          setCurrentLocation(loc);
          setVerifierForm(prev => ({
            ...prev,
            gps_coordinates: `${loc.latitude}, ${loc.longitude}`
          }));
          setLocationError(null);
        },
        (error) => {
          setLocationError('Unable to get GPS location. Please enable location services.');
          console.error('GPS error:', error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocationError('Geolocation not supported by browser');
    }
  };

  // Start camera capture - use front camera for selfie, back camera for others
  const startCapture = async (photoTypeId) => {
    try {
      setCaptureTarget(photoTypeId);
      setCapturing(true);
      
      // Use front camera for selfie photos, back camera for everything else
      const useFrontCamera = photoTypeId === 'fe_selfie' || photoTypeId.includes('selfie');
      const facingMode = useFrontCamera ? 'user' : 'environment';
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 720 } } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error('Unable to access camera');
      setCapturing(false);
    }
  };

  // Reverse geocode to get address from coordinates
  const reverseGeocode = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`
      );
      const data = await response.json();
      if (data && data.address) {
        const addr = data.address;
        return {
          street: addr.road || addr.neighbourhood || addr.suburb || '',
          city: addr.city || addr.town || addr.village || addr.county || '',
          state: addr.state || '',
          pincode: addr.postcode || '',
          country: addr.country || 'India'
        };
      }
    } catch (error) {
      console.error('Reverse geocode failed:', error);
    }
    return null;
  };

  // Format date for watermark
  const formatWatermarkDate = () => {
    const now = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const day = days[now.getDay()];
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const date = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${date} ${hours}:${minutes}:${seconds} ${day}`;
  };

  // Draw GPS watermark on canvas
  const drawGPSWatermark = async (ctx, canvas) => {
    if (!currentLocation) return;
    
    // Ensure latitude and longitude are numbers
    const latitude = parseFloat(currentLocation.latitude);
    const longitude = parseFloat(currentLocation.longitude);
    
    // Check if valid coordinates
    if (isNaN(latitude) || isNaN(longitude)) {
      console.warn('Invalid GPS coordinates:', currentLocation);
      return;
    }
    
    // Get address from coordinates
    let addressInfo = await reverseGeocode(latitude, longitude);
    
    // Calculate watermark dimensions
    const padding = 15;
    const lineHeight = 28;
    const headerHeight = 40;
    const totalLines = 3; // Date, Address, Lat-Long
    const watermarkHeight = headerHeight + (totalLines * lineHeight) + padding * 2;
    const startY = canvas.height - watermarkHeight;
    
    // Draw semi-transparent background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, startY, canvas.width, watermarkHeight);
    
    let currentY = startY + padding;
    
    // Header: Camera App
    ctx.fillStyle = '#e74c3c';
    ctx.fillRect(padding, currentY, 30, 25);
    ctx.fillStyle = 'white';
    ctx.font = 'bold 12px Arial';
    ctx.textBaseline = 'middle';
    ctx.fillText('JV', padding + 7, currentY + 12);
    
    ctx.fillStyle = '#e74c3c';
    ctx.font = 'bold 26px Arial';
    ctx.fillText('Camera App', padding + 40, currentY + 12);
    currentY += headerHeight;
    
    // Date-Time
    ctx.fillStyle = 'white';
    ctx.font = '20px Arial';
    ctx.fillText(`Date-Time: ${formatWatermarkDate()}`, padding, currentY);
    currentY += lineHeight;
    
    // Address
    if (addressInfo) {
      const addressStr = `Address: ${addressInfo.street}${addressInfo.street ? ', ' : ''}${addressInfo.city}, ${addressInfo.state} ${addressInfo.pincode}, ${addressInfo.country}`;
      ctx.fillText(addressStr, padding, currentY);
    } else {
      ctx.fillText('Address: Fetching location...', padding, currentY);
    }
    currentY += lineHeight;
    
    // Lat-Long
    ctx.fillText(`Lat-Long: (${latitude.toFixed(6)},${longitude.toFixed(6)})`, padding, currentY);
  };

  // Capture photo from camera
  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Add GPS watermark overlay
    await drawGPSWatermark(ctx, canvas);
    
    // Convert to blob and upload
    canvas.toBlob(async (blob) => {
      try {
        const formData = new FormData();
        formData.append('file', blob, `${captureTarget}_${Date.now()}.jpg`);
        formData.append('annexure_type', captureTarget);
        formData.append('description', getPhotoTypes().find(p => p.id === captureTarget)?.description || '');
        if (currentLocation) {
          formData.append('gps_latitude', currentLocation.latitude);
          formData.append('gps_longitude', currentLocation.longitude);
        }
        
        const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures/upload`, formData);
        setPhotos(prev => [...prev, res.data]);
        toast.success('Photo captured successfully');
      } catch (error) {
        toast.error('Failed to upload photo');
      }
      
      stopCapture();
    }, 'image/jpeg', 0.85);
  };

  // Stop camera
  const stopCapture = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setCapturing(false);
    setCaptureTarget(null);
  };

  // Handle photo upload via file from device - NO GPS watermark added
  // (FEs may upload pre-tagged photos from GPS Map Camera or similar apps)
  const handleUploadPhoto = async (photoTypeId, file) => {
    if (!file) {
      toast.error('Please select a photo');
      return;
    }
    
    try {
      setUploadingPhoto(photoTypeId);
      
      // Upload original file directly without adding GPS watermark
      // This preserves any existing GPS tags from external apps (GPS Map Camera, etc.)
      const formData = new FormData();
      formData.append('file', file, `${photoTypeId}_${Date.now()}.${file.name.split('.').pop() || 'jpg'}`);
      formData.append('annexure_type', photoTypeId);
      formData.append('description', getPhotoTypes().find(p => p.id === photoTypeId)?.description || '');
      
      // Still record device GPS for database reference (not added to image)
      if (currentLocation) {
        formData.append('gps_latitude', currentLocation.latitude);
        formData.append('gps_longitude', currentLocation.longitude);
      }
      
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures/upload`, formData);
      
      setPhotos(prev => [...prev, res.data]);
      toast.success('Photo uploaded successfully');
      setUploadingPhoto(null);
      
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
      setUploadingPhoto(null);
    }
  };

  // Delete photo
  const handleDeletePhoto = async (annexureId) => {
    try {
      await axios.delete(`/cases/${caseId}/checks/${checkId}/annexures/${annexureId}`);
      setPhotos(prev => prev.filter(p => p.id !== annexureId));
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Submit verification
  const handleSubmitVerification = async () => {
    // Veecheck Jun 2026 — Field Executive Name is mandatory. This value
    // is what appears as "Executive Name" on the PDF when the vendor
    // portal didn't pre-fill a Field Agent Name on assignment.
    if (!verifierForm.fe_name || !verifierForm.fe_name.trim()) {
      toast.error('Please enter your Field Executive Name');
      return;
    }
    // Validate ethics declaration
    if (!verifierForm.ethics_declaration) {
      toast.error('Please accept the ethics declaration');
      return;
    }

    // Validate residence_confirmed selection first
    if (!verifierForm.residence_confirmed) {
      toast.error('Please select Residence Confirmed at Address?');
      return;
    }

    // Veecheck Jun 2026 — Photo requirements depend on residence outcome:
    //  • "Yes - Candidate resides here" → ALL required photos must be present
    //    (House Photo 1+2, Landmark Photo 1+2, FE Selfie, Verifier ID Proof).
    //  • Any exception outcome (Left Job, Not Responding, Incorrect Number,
    //    etc.) → only 2 photos required (House Photo 1 + Landmark Photo 1)
    //    since the verifier often can't physically inspect the residence.
    const uploadedTypes = photos.map(p => p.annexure_type);
    const EXCEPTION_OUTCOMES = [
      'left_job_not_cooperating', 'left_job',
      'not_responding_call', 'not_responding',
      'incorrect_number_switch_off', 'incorrect_number', 'incorrect_number_switchoff',
      'not_traceable',
      'shifted', 'shifted_to_new_address',
      'direct_visit_not_located_address', 'direct_visit_not_located',
      'no',
      // Veecheck Jun 28, 2026 — Company/Institute outcomes are treated as
      // exceptions so only the minimum 2 proof photos are required (FE
      // can't always inspect the residence on a company-premises visit).
      'company_found_not_cooperative',
      'company_not_found',
      'company_verified',
    ];
    const isException = EXCEPTION_OUTCOMES.includes(verifierForm.residence_confirmed);
    let requiredPhotoIds;
    if (isException) {
      // Exception path — only minimum proof of visit needed.
      requiredPhotoIds = ['house_photo_1', 'landmark_photo_1'];
    } else {
      // Standard "Yes" / full verification path.
      requiredPhotoIds = getPhotoTypes().filter(p => p.required).map(p => p.id);
    }
    const missingPhotoIds = requiredPhotoIds.filter(id => !uploadedTypes.includes(id));
    if (missingPhotoIds.length > 0) {
      const labelMap = Object.fromEntries(getPhotoTypes().map(p => [p.id, p.label]));
      toast.error(`Please upload required photos: ${missingPhotoIds.map(id => labelMap[id] || id).join(', ')}`);
      return;
    }
    if (verifierForm.residence_confirmed === 'yes') {
      const yesRequired = [
        ['person_met', 'Person Met / Verifier Name'],
        ['relationship', 'Relationship with Candidate'],
        ['residence_type', 'Type of Residence'],
        ['building_type', 'House/Building Type'],
        ['period_of_stay_from', 'Period of Stay (From)'],
        ['period_of_stay_to', 'Period of Stay (To)'],
      ];
      const missing = yesRequired
        .filter(([k]) => !verifierForm[k] || String(verifierForm[k]).trim() === '')
        .map(([, label]) => label);
      if (missing.length > 0) {
        toast.error(`Please fill required fields: ${missing.join(', ')}`);
        return;
      }
    }

    // Veecheck Jun 2026 — For exception outcomes the only mandatory inputs
    // are the 2 photos already validated above. Skip ALL other field
    // validations (verifier name, person met, residence details, ethics
    // declaration is still enforced earlier in the flow).
    if (isException) {
      // Save whatever verifier name was provided (optional) and submit.
      const verifierNameOptional = verifierForm.verifier_name || verifierForm.person_met || 'Field Verifier';
      localStorage.setItem('fe_name', verifierNameOptional);
    } else {
      // Validate verifier name - use person_met as the verifier name
      const verifierName = verifierForm.verifier_name || verifierForm.person_met;
      if (!verifierName) {
        toast.error('Please enter your name (Person Met / Verifier Name)');
        return;
      }
      // Save FE details locally
      localStorage.setItem('fe_name', verifierName);
    }
    localStorage.setItem('fe_phone', verifierForm.person_contact || verifierForm.verifier_contact);
    
    setSubmitting(true);
    try {
      // Veecheck Jun 2026 — verifier_name on the PDF is the Field
      // Executive's own name (NOT the person they met). The FE-entered
      // fe_name is the source of truth; we still populate the legacy
      // verifier_name field for back-compat.
      const verifierName = (verifierForm.fe_name && verifierForm.fe_name.trim())
        || verifierForm.verifier_name
        || 'Field Verifier';
      await axios.post(`/field-verification/${caseId}/${checkId}/submit`, {
        ...verifierForm,
        verifier_name: verifierName,  // Ensure verifier_name is set
        fe_name: verifierForm.fe_name || verifierName,
        photos: photos.map(p => ({ 
          id: p.id, 
          type: p.annexure_type,
          url: p.file_url || p.url,
          label: p.annexure_type?.replace(/_/g, ' '),
          gps_coordinates: p.gps_latitude && p.gps_longitude ? `${p.gps_latitude}, ${p.gps_longitude}` : null,
          gps_latitude: p.gps_latitude,
          gps_longitude: p.gps_longitude
        })),
        check_category: checkCategory,
        submitted_at: new Date().toISOString()
      });
      // Veecheck Jun 2026 — show a prominent, longer-lived success
      // toast so the FE clearly sees confirmation before the redirect
      // to "My Assignments". Previously the redirect fired in the same
      // tick as the toast, and the FE was sometimes unsure whether the
      // submission had actually gone through.
      const candidateLabel = employeeData?.name
        || caseData?.employee_name
        || 'this candidate';
      toast.success('Verification submitted successfully!', {
        description: `Saved for ${candidateLabel} (${checkId}). Redirecting to My Assignments…`,
        duration: 5000,
      });

      // Show dashboard after the FE has had a chance to read the toast.
      setTimeout(() => {
        setShowDashboard(true);
      }, 1800);
    } catch (error) {
      toast.error('Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const getPhotoStatus = (photoType) => {
    return photos.find(p => p.annexure_type === photoType);
  };

  // Fetch FE assignments for dashboard
  const fetchFeAssignments = async () => {
    if (!verifierForm.verifier_name) return;
    try {
      const res = await axios.get(`/field-verification/fe-assignments`, {
        params: { fe_name: verifierForm.verifier_name }
      });
      setFeAssignments(res.data || []);
    } catch (error) {
      console.error('Failed to fetch FE assignments:', error);
    }
  };

  useEffect(() => {
    if (showDashboard && verifierForm.verifier_name) {
      fetchFeAssignments();
    }
  }, [showDashboard, verifierForm.verifier_name]);

  // Get check type icon
  const getCheckIcon = () => {
    switch (checkCategory) {
      case 'employment': return <Briefcase className="w-5 h-5 text-blue-600" />;
      case 'education': return <GraduationCap className="w-5 h-5 text-purple-600" />;
      case 'criminal': return <FileCheck className="w-5 h-5 text-red-600" />;
      default: return <Home className="w-5 h-5 text-teal-600" />;
    }
  };

  // Veecheck Jun 28, 2026 — Read-only mode: when the check is already
  // completed (verified/auto-completed), the form must still SHOW all
  // previously entered data so QC/FE can review what was submitted, but
  // it must NOT allow further edits. Wrapping the entire form in a
  // <fieldset disabled> cascades the disabled state to every Input /
  // Select / Textarea / Button inside without touching each component.
  const _checkStatus = (checkData && (checkData.status || checkData.verification_progress || '')) || '';
  const isReadOnly = ['verified', 'completed', 'cleared'].includes(String(_checkStatus).toLowerCase())
    || checkData?.auto_completed === true
    || checkData?.verification_progress === 'completed';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Clock className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading verification details...</p>
        </div>
      </div>
    );
  }

  // FE Dashboard View
  if (showDashboard) {
    const pendingCount = feAssignments.filter(a => a.status !== 'completed').length;
    const completedCount = feAssignments.filter(a => a.status === 'completed').length;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100">
        <div className="bg-teal-600 text-white p-4 shadow-lg">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <List className="w-8 h-8" />
                <div>
                  <h1 className="text-xl font-bold">My Assignments</h1>
                  <p className="text-teal-100 text-sm">{verifierForm.verifier_name}</p>
                </div>
              </div>
              <Button size="sm" variant="secondary" onClick={() => setShowDashboard(false)}>
                <Plus className="w-4 h-4 mr-1" />New
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto p-4 space-y-3">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{feAssignments.length}</p>
                <p className="text-xs text-blue-600">Total</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
                <p className="text-xs text-amber-600">Pending</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="py-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{completedCount}</p>
                <p className="text-xs text-emerald-600">Completed</p>
              </CardContent>
            </Card>
          </div>
          
          <Button variant="outline" className="w-full" onClick={fetchFeAssignments}>
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          
          {feAssignments.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="font-medium">No assignments found</p>
                <p className="text-sm mt-1">Assignments will appear here when assigned by vendor</p>
              </CardContent>
            </Card>
          ) : (
            feAssignments.map((assignment, idx) => (
              <Card 
                key={assignment.check_id || idx}
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  assignment.status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
                  assignment.status === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                  'bg-white border-gray-200'
                }`}
                onClick={() => {
                  setShowDashboard(false);
                  navigate(`/field-verification/${assignment.case_id}/${assignment.check_id}`);
                }}
              >
                <CardContent className="py-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">{assignment.employee_name}</p>
                    <Badge className={
                      assignment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      assignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }>
                      {assignment.status === 'completed' ? 'Completed' : 
                       assignment.status === 'in_progress' ? 'In Progress' : 'Pending'}
                    </Badge>
                  </div>
                  <div className="space-y-1 text-sm text-gray-500">
                    <p className="flex items-center gap-2">
                      <FileCheck className="w-3 h-3" />
                      {(assignment.check_type || 'Address Check').replace(/_/g, ' ')}
                    </p>
                    {assignment.address && (
                      <p className="flex items-center gap-2 truncate">
                        <MapPin className="w-3 h-3" />
                        {assignment.address}
                      </p>
                    )}
                    <p className="flex items-center gap-2 text-xs text-gray-400">
                      Case: {assignment.case_id}
                    </p>
                    {assignment.assigned_at && (
                      <p className="flex items-center gap-2 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        Assigned: {(() => { const d = new Date(assignment.assigned_at); if (Number.isNaN(d.getTime())) return '-'; const pad = n => String(n).padStart(2,'0'); return `${pad(d.getDate())}-${pad(d.getMonth()+1)}-${d.getFullYear()}`; })()}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100">
      {/* Header */}
      <div className="bg-teal-600 text-white p-4 shadow-lg">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getCheckIcon()}
              <div>
                <h1 className="text-xl font-bold">Field Verification</h1>
                <p className="text-teal-100 text-sm capitalize">{checkCategory} Verification</p>
              </div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setShowDashboard(true)}>
              <List className="w-4 h-4 mr-1" />My Tasks
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Insufficiency Block Warning */}
        {insufficiencyBlocked && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-800">Insufficiency Raised</p>
                  <p className="text-sm text-red-700 mt-1">This case has an open insufficiency. Please ask admin to clear the insufficiency before updating the field form.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!insufficiencyBlocked && (<>
        {/* Veecheck Jun 28, 2026 — Read-only banner when the check is
            already completed. Shows the FE/QC the form is locked. */}
        {isReadOnly && (
          <Card className="border-blue-300 bg-blue-50">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-blue-800 text-sm">Verification Completed — View Only</p>
                  <p className="text-xs text-blue-700 mt-0.5">This case is already verified. Form data is shown for reference but cannot be edited.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <fieldset disabled={isReadOnly} className={isReadOnly ? 'opacity-95 [&_button]:cursor-not-allowed [&_input]:cursor-not-allowed [&_textarea]:cursor-not-allowed' : ''}>
        {/* GPS Status */}
        <Card className={currentLocation ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Navigation className={`w-5 h-5 ${currentLocation ? 'text-emerald-600' : 'text-amber-600'}`} />
                <div>
                  <p className="text-sm font-medium">
                    {currentLocation ? 'GPS Location Captured' : 'GPS Location Required'}
                  </p>
                  {currentLocation && (
                    <p className="text-xs text-emerald-700">
                      {currentLocation.latitude}, {currentLocation.longitude}
                    </p>
                  )}
                  {locationError && (
                    <p className="text-xs text-amber-700">{locationError}</p>
                  )}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={refreshLocation}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Case Info */}
        {/* Header with Logo, Check Name and Check ID */}
        <div className="bg-indigo-600 text-white p-4 rounded-lg shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h2 className="text-xl font-bold">
                {(() => {
                  const type = checkData?.check_type || checkCategory || '';
                  const subType = checkData?.subcheck_type || employeeData?.address_type || '';
                  
                  // Build complete check type name
                  if (type.includes('address')) {
                    return `Address ${subType || 'Present'}`;
                  } else if (type.includes('employment')) {
                    return subType?.toLowerCase().includes('latest') ? 'Employment Latest' : 
                           subType?.toLowerCase().includes('previous') ? 'Employment Previous' : 
                           `Employment ${subType || 'Latest'}`;
                  } else if (type.includes('education')) {
                    return subType?.toLowerCase().includes('highest') ? 'Education Highest' :
                           subType?.toLowerCase().includes('latest') ? 'Education Latest' :
                           `Education ${subType || 'Highest'}`;
                  } else if (type.includes('criminal')) {
                    return 'Criminal Verification';
                  } else {
                    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Verification Check';
                  }
                })()}
              </h2>
              {/* Joining Company Name (client-provided) — fallback to client_name */}
              <p className="text-white font-bold text-base mt-2">
                {caseData?.joining_company_name || caseData?.client_name || 'Company Name'}
              </p>
              <p className="text-indigo-100 text-sm mt-1">
                Check ID: {checkId}
              </p>
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_veecheck-staging/artifacts/w5naci8s_Veecheck%20logo.png" 
              alt="Veecheck" 
              className="h-16 w-auto rounded bg-white p-1.5 shadow-md"
            />
          </div>
        </div>

        {/* Veecheck Jun 2026 — sticky progress bar so the FE can see how
            close they are to a complete, valid submission. Counts the
            key required fields incrementally; the page-scrollable form
            puts the bar at the top so it's visible during scroll. */}
        <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-white/85 backdrop-blur border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-semibold text-slate-700">Form Progress</span>
            <span data-testid="form-progress-percent" className="font-mono text-slate-700">{progressPct}%</span>
          </div>
          <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
            <div
              data-testid="form-progress-bar"
              className={`h-full transition-all duration-300 ${progressPct === 100 ? 'bg-emerald-500' : 'bg-teal-500'}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Case Details - without Check ID */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              Case Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div className="text-gray-500">Case ID:</div>
              <div className="font-medium">{caseId}</div>
              <div className="text-gray-500">Employee:</div>
              <div className="font-medium">{employeeData?.name || checkData?.employee_name || '-'}</div>
              <div className="text-gray-500">Father Name:</div>
              <div className="font-medium">{employeeData?.father_name || '-'}</div>
            </div>
            
            {/* Address Details Section */}
            <div className="pt-2 border-t space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {employeeData?.address_type && (
                  <>
                    <div className="text-gray-500">Address Type:</div>
                    <div className="font-medium">{employeeData.address_type}</div>
                  </>
                )}
                <div className="text-gray-500">Address:</div>
                <div className="font-medium">{employeeData?.address || checkData?.address || '-'}</div>
                <div className="text-gray-500">City:</div>
                <div className="font-medium">{employeeData?.city || '-'}</div>
                <div className="text-gray-500">Landmark:</div>
                <div className="font-medium">{employeeData?.landmark || '-'}</div>
                <div className="text-gray-500">State:</div>
                <div className="font-medium">{employeeData?.state || '-'}</div>
                <div className="text-gray-500">Pincode:</div>
                <div className="font-medium">{employeeData?.pincode || '-'}</div>
                <div className="text-gray-500">Contact:</div>
                <div className="font-medium">{employeeData?.phone || '-'}</div>
                <div className="text-gray-500">Alt Contact:</div>
                <div className="font-medium">{employeeData?.alternate_contact || '-'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Special Instructions Box */}
        {checkData?.special_instructions && (
          <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Special Instructions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-bold text-gray-800 whitespace-pre-wrap">
                {checkData.special_instructions}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Additional Remarks Box */}
        {(checkData?.remarks || employeeData?.remarks) && (
          <Card className="border-blue-300 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                <AlertTriangle className="w-5 h-5" />
                Additional Remarks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-bold text-gray-800 whitespace-pre-wrap">
                {checkData?.remarks || employeeData?.remarks}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Verifier Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-teal-600" />
              Verifier Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 1. Residence Confirmed at Address? */}
            <div>
              <Label>Residence Confirmed at Address? *</Label>
              <Select 
                value={verifierForm.residence_confirmed}
                onValueChange={(v) => setVerifierForm(prev => ({ ...prev, residence_confirmed: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes - Candidate resides here</SelectItem>
                  <SelectItem value="no">No - Candidate does not reside here</SelectItem>
                  <SelectItem value="shifted">Shifted to new address</SelectItem>
                  <SelectItem value="not_traceable">Not Traceable</SelectItem>
                  <SelectItem value="left_job_not_cooperating">Left Job / Not Co-operating</SelectItem>
                  <SelectItem value="incorrect_number_switchoff">Incorrect Number / Switch Off</SelectItem>
                  <SelectItem value="not_responding_call">Not Responding Call</SelectItem>
                  <SelectItem value="direct_visit_not_located">Direct Visit - Not Located Address</SelectItem>
                  {/* Veecheck Jun 28, 2026 — Company/Institute outcomes
                      for cases where the FE visits an employer/college
                      premises instead of a personal residence. These
                      DO NOT auto-complete the check — the server-side
                      auto-green logic only fires on residence_confirmed === 'yes'. */}
                  <SelectItem value="company_found_not_cooperative">Company/Institute Found - Not Co-operative</SelectItem>
                  <SelectItem value="company_not_found">Company/Institute - Not Found</SelectItem>
                  <SelectItem value="company_verified">Company/Institute Verified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* 2. Person Met / Verifier Name - Always visible, syncs with Verification Update form */}
            <div>
              <Label>
                Person Met / Verifier Name
                {verifierForm.residence_confirmed === 'yes' && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Input 
                value={verifierForm.person_met}
                onChange={(e) => handleTextInput('person_met', e.target.value)}
                placeholder="Name of person met during verification"
                style={{ textTransform: 'capitalize' }}
              />
            </div>
            
            {/* 3. Relationship with Candidate */}
            <div>
              <Label>
                Relationship with Candidate
                {verifierForm.residence_confirmed === 'yes' && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Select 
                value={verifierForm.relationship}
                onValueChange={(v) => setVerifierForm(prev => ({ ...prev, relationship: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Self</SelectItem>
                  <SelectItem value="father">Father</SelectItem>
                  <SelectItem value="mother">Mother</SelectItem>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="sibling">Sibling</SelectItem>
                  <SelectItem value="relative">Relative</SelectItem>
                  <SelectItem value="neighbor">Neighbor</SelectItem>
                  <SelectItem value="landlord">Landlord</SelectItem>
                  <SelectItem value="security">Security Guard</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* 4. Your Contact Number - Not Mandatory */}
            <div>
              <Label>Your Contact Number</Label>
              <Input 
                value={verifierForm.person_contact}
                onChange={(e) => setVerifierForm(prev => ({ ...prev, person_contact: e.target.value }))}
                placeholder="+91-XXXXXXXXXX"
              />
            </div>
          </CardContent>
        </Card>

        {/* Verification Details - Only for Address/Criminal */}
        {(checkCategory === 'address' || checkCategory === 'criminal') && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Home className="w-5 h-5 text-teal-600" />
                Verification Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 1. Type of Residence */}
              <div>
                <Label>
                  Type of Residence
                  {verifierForm.residence_confirmed === 'yes' && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Select 
                  value={verifierForm.residence_type}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, residence_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owned">Self-Owned</SelectItem>
                    <SelectItem value="rented">Rented</SelectItem>
                    <SelectItem value="family_owned">Family Owned</SelectItem>
                    <SelectItem value="company_provided">Company Provided</SelectItem>
                    <SelectItem value="paying_guest">Paying Guest (PG)</SelectItem>
                    <SelectItem value="hostel">Hostel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* 2. House/Building Type */}
              <div>
                <Label>
                  House/Building Type
                  {verifierForm.residence_confirmed === 'yes' && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Select 
                  value={verifierForm.building_type}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, building_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="independent_house">Independent House</SelectItem>
                    <SelectItem value="apartment">Apartment/Flat</SelectItem>
                    <SelectItem value="villa">Villa/Bungalow</SelectItem>
                    <SelectItem value="row_house">Row House</SelectItem>
                    <SelectItem value="chawl">Chawl/Tenement</SelectItem>
                    <SelectItem value="slum">Slum Area</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* 3. Address Type (Locality Type) */}
              <div>
                <Label>Locality Type</Label>
                <Select 
                  value={verifierForm.address_type}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, address_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="posh">Posh/Upscale</SelectItem>
                    <SelectItem value="middle_class">Middle Class</SelectItem>
                    <SelectItem value="lower_middle">Lower Middle Class</SelectItem>
                    <SelectItem value="industrial">Industrial Area</SelectItem>
                    <SelectItem value="rural">Rural/Village</SelectItem>
                    <SelectItem value="slum">Slum Area</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* 4. Period of Stay - Split to From & To */}
              <div>
                <Label>
                  Period of Stay
                  {verifierForm.residence_confirmed === 'yes' && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-gray-500">From</span>
                    <Input 
                      type="month"
                      value={verifierForm.period_of_stay_from}
                      onChange={(e) => setVerifierForm(prev => ({ ...prev, period_of_stay_from: e.target.value }))}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">To</span>
                    <Input 
                      type="month"
                      value={verifierForm.period_of_stay_to}
                      onChange={(e) => setVerifierForm(prev => ({ ...prev, period_of_stay_to: e.target.value }))}
                      placeholder="Present"
                    />
                  </div>
                </div>
              </div>
              
              {/* Veecheck Jun 2026 — Nearest Police Station + AI suggest button.
                  Uses Gemini (gemini-2.5-flash) via the EMERGENT_LLM_KEY on
                  the backend to look up the local police station from the
                  candidate's pincode / city / state. Verifier can edit or
                  accept the suggestion. */}
              <div>
                <Label>Nearest Police Station</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="nearest-police-station-input"
                    value={verifierForm.nearest_police_station}
                    onChange={(e) => handleTextInput('nearest_police_station', e.target.value)}
                    placeholder="e.g. Miyapur Police Station"
                    style={{ textTransform: 'capitalize' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="suggest-police-station-btn"
                    disabled={suggestingPoliceStation}
                    onClick={async () => {
                      // Veecheck Jun 2026 — the public field-verification
                      // endpoint exposes the address pincode at multiple
                      // paths depending on how the case was ingested. We
                      // try every known location so the AI button works
                      // for both new and in-progress cases.
                      const c = checkData || {};
                      const emp = employeeData || {};
                      const sub = c.sub_option === 'permanent' ? 'permanent' : 'present';
                      const pincode = (c.address_data && c.address_data.pincode)
                        || (c.subcheck_data && c.subcheck_data.pincode)
                        || (c.subcheck_data && c.subcheck_data.pin_code)
                        || emp.pincode
                        || (emp[`${sub}_address`] && emp[`${sub}_address`].pincode)
                        || (emp.present_address && emp.present_address.pincode)
                        || (emp.permanent_address && emp.permanent_address.pincode)
                        || '';
                      const city = (c.address_data && c.address_data.city)
                        || (c.subcheck_data && c.subcheck_data.city)
                        || emp.city
                        || (emp[`${sub}_address`] && emp[`${sub}_address`].city)
                        || '';
                      const stateVal = (c.address_data && c.address_data.state)
                        || (c.subcheck_data && c.subcheck_data.state)
                        || emp.state
                        || (emp[`${sub}_address`] && emp[`${sub}_address`].state)
                        || '';
                      const address = (c.address_data && (c.address_data.complete_address || c.address_data.line1))
                        || (c.subcheck_data && (c.subcheck_data.complete_address || c.subcheck_data.address))
                        || emp.address
                        || (emp[`${sub}_address`] && (emp[`${sub}_address`].complete_address || emp[`${sub}_address`].address_line1))
                        || '';
                      if (!pincode && !address) {
                        toast.error('No address / pincode available on this case to look up.');
                        return;
                      }
                      setSuggestingPoliceStation(true);
                      try {
                        const r = await axios.post('/address-verification/suggest-police-station', {
                          pincode, city, state: stateVal, address,
                        });
                        const sugg = r.data?.suggestion || '';
                        if (!sugg) {
                          toast.info('AI could not confidently identify a police station for this pincode.');
                        } else {
                          setVerifierForm(prev => ({ ...prev, nearest_police_station: sugg }));
                          toast.success(`Suggested: ${sugg}`);
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'AI lookup failed');
                      } finally {
                        setSuggestingPoliceStation(false);
                      }
                    }}
                  >
                    {suggestingPoliceStation ? '...' : '✨ AI'}
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">AI-suggested from pincode. Please verify before submitting.</p>
              </div>

              {/* Marital Status — captured here so it renders on the PDF
                  alongside Nearest Police Station (was previously NA). */}
              <div>
                <Label>Candidate&apos;s Marital Status</Label>
                <Select
                  value={verifierForm.marital_status}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, marital_status: v }))}
                >
                  <SelectTrigger data-testid="marital-status-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single">Single</SelectItem>
                    <SelectItem value="Married">Married</SelectItem>
                    <SelectItem value="Divorced">Divorced</SelectItem>
                    <SelectItem value="Widowed">Widowed</SelectItem>
                    <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 5. Neighbor Confirmed Candidate's Residence? */}
              <div>
                <Label>Neighbor Confirmed Candidate's Residence?</Label>
                <Select 
                  value={verifierForm.neighbor_confirmed}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, neighbor_confirmed: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="na">Not Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Neighbor name if confirmed */}
              {verifierForm.neighbor_confirmed === 'yes' && (
                <div>
                  <Label>Neighbor's Name</Label>
                  <Input 
                    value={verifierForm.neighbor_name}
                    onChange={(e) => handleTextInput('neighbor_name', e.target.value)}
                    placeholder="Name of neighbor who confirmed"
                    style={{ textTransform: 'capitalize' }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Overall Remarks */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Remarks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea 
              value={verifierForm.overall_remarks}
              onChange={(e) => {
                // Capitalize first letter of sentences
                const value = e.target.value;
                const capitalizedValue = value.replace(/(^\w|\.\s+\w)/g, (char) => char.toUpperCase());
                setVerifierForm(prev => ({ ...prev, overall_remarks: capitalizedValue }));
              }}
              placeholder="Any additional observations or comments..."
              rows={3}
              style={{ textTransform: 'none' }}
            />
          </CardContent>
        </Card>

        {/* Photo Capture Section — placed after Remarks, just before the Ethics Declaration so the FE captures photos as the final step (Veecheck Jun 2026 — user-requested order). */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-teal-600" />
              Photo Capture
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Veecheck Jun 2026 — Camera preview now renders INSIDE the
                row whose Take Photo button was clicked (see below) rather
                than at the top of the section, so the FE doesn't have to
                scroll back up after taking 3 photos.  Hidden canvas
                stays here at the top so capturePhoto() can keep its
                single canvasRef. */}
            <canvas ref={canvasRef} className="hidden" />

            {getPhotoTypes().map((photoType) => {
              const uploaded = getPhotoStatus(photoType.id);
              const isActiveCapture = capturing && captureTarget === photoType.id;
              return (
                <div key={photoType.id} className={`p-3 rounded-lg border ${uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        {uploaded ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <ImageIcon className="w-4 h-4 text-gray-400" />}
                        {photoType.label}
                        {photoType.required && <span className="text-red-500">*</span>}
                      </p>
                      <p className="text-xs text-gray-500">{photoType.description}</p>
                    </div>
                    {uploaded && (
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleViewPhoto({ ...uploaded, photoTypeLabel: photoType.label })}
                          title="Preview photo"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-amber-600" 
                          onClick={() => handleRetakePhoto(uploaded.id, photoType.id)}
                          title="Retake photo"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Veecheck Jun 2026 — inline camera preview rendered
                      INSIDE the active row so the FE doesn't have to
                      scroll back up after taking 3 photos to find the
                      camera for the next type (selfie etc). Only one
                      row is ever active at a time, so the shared
                      videoRef is fine. */}
                  {isActiveCapture && (
                    <div className="relative bg-black rounded-lg overflow-hidden mb-2">
                      <video ref={videoRef} autoPlay playsInline className="w-full" />
                      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3 px-3">
                        <Button
                          onClick={capturePhoto}
                          className="bg-white text-black hover:bg-gray-100"
                          data-testid={`capture-${photoType.id}-btn`}
                        >
                          <Camera className="w-5 h-5 mr-2" />Capture
                        </Button>
                        <Button onClick={stopCapture} variant="destructive">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {!uploaded && !capturing && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="flex-1 bg-teal-600 hover:bg-teal-700"
                          onClick={() => startCapture(photoType.id)}
                        >
                          <Camera className="w-4 h-4 mr-1" />Capture
                        </Button>
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUploadPhoto(photoType.id, file);
                              e.target.value = ''; // Reset input
                            }}
                          />
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full"
                            type="button"
                            onClick={(e) => e.currentTarget.parentElement.querySelector('input').click()}
                            disabled={uploadingPhoto === photoType.id}
                          >
                            {uploadingPhoto === photoType.id ? (
                              <>Uploading...</>
                            ) : (
                              <><Upload className="w-4 h-4 mr-1" />Upload</>
                            )}
                          </Button>
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {uploaded?.gps_latitude && uploaded?.gps_longitude && (
                    <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                      <Navigation className="w-3 h-3" />
                      GPS: {uploaded.gps_latitude}, {uploaded.gps_longitude}
                    </p>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Ethics Declaration */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 space-y-4">
            {/* Veecheck Jun 2026 — Field Executive Name (mandatory).
                When the vendor portal didn't pre-fill a Field Agent Name on
                assignment, this is what renders as "Executive Name" on the
                BGV PDF (no longer falls back to Person Met). */}
            <div>
              <Label htmlFor="fe-name" className="text-sm font-semibold">
                Field Executive Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="fe-name"
                data-testid="fe-name-input"
                value={verifierForm.fe_name}
                onChange={(e) => handleTextInput('fe_name', e.target.value)}
                placeholder="Your full name (as the executive who conducted this visit)"
                style={{ textTransform: 'capitalize' }}
                className="bg-white"
              />
              <p className="text-[10px] text-slate-600 mt-1">
                {checkData?.field_agent_name && verifierForm.fe_name === (checkData.field_agent_name || '').trim()
                  ? `Pre-filled from vendor assignment. You can edit if you're a different executive.`
                  : 'Used as the Executive Name on the verification report.'}
              </p>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="ethics"
                checked={verifierForm.ethics_declaration}
                onCheckedChange={(checked) => setVerifierForm(prev => ({ ...prev, ethics_declaration: checked }))}
                className="mt-1"
              />
              <Label htmlFor="ethics" className="text-sm leading-relaxed cursor-pointer">
                I hereby declare that I have conducted this verification in person, following all ethical guidelines. 
                The information and photographs submitted are genuine and captured at the actual location. 
                I have not fabricated or manipulated any data in this report.
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button — hidden when the form is read-only (completed). */}
        {!isReadOnly && (
        <Button 
          className="w-full bg-teal-600 hover:bg-teal-700 h-12 text-lg"
          onClick={handleSubmitVerification}
          disabled={submitting || !verifierForm.ethics_declaration}
        >
          {submitting ? (
            <>
              <Clock className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Submit Verification
            </>
          )}
        </Button>
        )}

        {!isReadOnly && !verifierForm.ethics_declaration && (
          <p className="text-center text-sm text-amber-600">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Please accept the ethics declaration before submitting
          </p>
        )}
        </fieldset>

        {/* Photo Preview Modal */}
        {showPreviewModal && previewPhoto && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowPreviewModal(false);
              setPreviewPhoto(null);
            }}
          >
            <div 
              className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-3 border-b flex items-center justify-between bg-gray-50">
                <div>
                  <h3 className="font-semibold text-gray-800">{previewPhoto.photoTypeLabel || 'Photo Preview'}</h3>
                  <p className="text-xs text-gray-500">{previewPhoto.description || ''}</p>
                </div>
                <button 
                  type="button"
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPreviewModal(false);
                    setPreviewPhoto(null);
                  }}
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="p-2 bg-gray-100">
                <img 
                  src={previewPhoto.file_url} 
                  alt={previewPhoto.photoTypeLabel || 'Captured photo'}
                  className="w-full h-auto max-h-[60vh] object-contain rounded"
                />
              </div>
              {previewPhoto.gps_latitude && previewPhoto.gps_longitude && (
                <div className="px-3 py-2 text-xs text-gray-600 bg-gray-50 flex items-center gap-2">
                  <Navigation className="w-3 h-3" />
                  GPS: {previewPhoto.gps_latitude}, {previewPhoto.gps_longitude}
                </div>
              )}
              <div className="p-3 border-t flex justify-between">
                <button 
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPreviewModal(false);
                    setPreviewPhoto(null);
                  }}
                >
                  Close
                </button>
                <Button 
                  type="button"
                  variant="destructive"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRetakePhoto(previewPhoto.id, previewPhoto.annexure_type);
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retake Photo
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 py-4">
          <p>Veecheck Field Verification App</p>
          <p>Powered by Background Verification CRM</p>
        </div>
        </>)}
      </div>
    </div>
  );
}
